/**
 * Mission 4's officer (02 §9.1, 03 §2): he gives up to a weapon pointed at him from two
 * metres when no alerted guard is close enough to save him, follows as a captive, bolts if
 * left alone or if his captor goes down, and at alert 3 runs for the north edge.
 */

import { type Ctx, isAlive, isFighter } from '../ctx';
import { angleDiff, atan2, PI } from '../math';
import { ENEMY, OBJECTIVES } from '../tuning';
import type { Enemy } from '../types';
import { dist, failObjective, findRule, isPrisoner, loseCaptive, targetName } from './common';

/** `stepFlee` gives up when its timer runs out; a fleeing officer's is kept topped up to this. */
const FLEE_KEEPALIVE_TICKS = 600;

/** The officer still at large, or null (no such mission, already taken, already gone). */
export function officerAtLarge(ctx: Pick<Ctx, 'world'>): Enemy | null {
  const id = ctx.world.objective.officerId;
  if (id === -1) return null;
  return ctx.world.enemies.find((e) => e.id === id) ?? null;
}

/** The officer, if he would surrender right now (02 §9.1). */
export function officerWouldSurrender(ctx: Pick<Ctx, 'world'>): Enemy | null {
  const w = ctx.world;
  const r = w.ranger;
  const e = officerAtLarge(ctx);
  if (!e || !isAlive(e) || r.status !== 'active') return null;
  const dx = e.x - r.x;
  const dz = e.z - r.z;
  if (dx * dx + dz * dz > OBJECTIVES.surrenderRange * OBJECTIVES.surrenderRange) return null;
  if (Math.abs(angleDiff(r.aim, atan2(dx, dz))) > (OBJECTIVES.surrenderAimDeg * PI) / 180) return null;
  // With an alerted guard at his shoulder he takes his chances instead.
  for (const g of w.enemies) {
    if (g.id === e.id || !isFighter(g) || g.state !== 'combat') continue;
    if (dist(g.x, g.z, e.x, e.z) <= OBJECTIVES.surrenderGuardRadius) return null;
  }
  return e;
}

/** He puts his hands up: from here on he is a captive, not a soldier. */
export function captureOfficer(ctx: Ctx, e: Enemy): void {
  const w = ctx.world;
  const o = w.objective;
  const index = w.enemies.indexOf(e);
  if (index < 0) return;
  w.enemies.splice(index, 1);
  if (w.runnerId === e.id) w.runnerId = -1;
  if (w.ranger.dragging === e.id) w.ranger.dragging = -1;
  // What he is once taken is the spawn's to say: the scientist of mission 30 is a prisoner like any officer.
  const kind = ctx.statics.def.spawns.find((s) => s.captureTarget === true)?.captiveKind ?? 'officer';
  o.captives.push({
    id: e.id, kind, x: e.x, z: e.z, prevX: e.x, prevZ: e.z, facing: e.facing,
    state: 'following', travelled: e.travelled, moving: false,
    // Shot on the way in, or carried off the floor: either way he scores as a wounded prisoner (02 §11).
    wounded: e.hp < ENEMY.hp[e.kind] || e.state === 'wounded',
    climbTicks: 0, awayTicks: 0, path: [], pathIndex: 0, stuckTicks: 0,
  });
  o.officerFleeing = false;
  o.surrenderTicks = 0;
  ctx.events.push({ type: 'captive', id: e.id, what: 'surrendered' });
  // The pickup comes to him (01 §8) - unless the Osprey is already on its way to the old one.
  if (findRule(ctx.statics.def, 'recall')?.mode === 'relocatesToCapture' && w.osprey.phase === 'away') {
    o.pickupX = e.x;
    o.pickupZ = e.z;
  }
}

/** Find the `captureTarget` among the garrison, once, by the post he was given. */
function resolveOfficer(ctx: Ctx): void {
  const o = ctx.world.objective;
  if (o.officerId !== -1 || o.groundTicks > 0) return;
  const spawn = ctx.statics.def.spawns.find((s) => s.captureTarget === true && s.dormantUntil === undefined);
  if (!spawn) return;
  const e = ctx.world.enemies.find((en) => en.kind === spawn.kind && en.homeX === spawn.x && en.homeZ === spawn.z);
  if (e) o.officerId = e.id;
}

export function stepOfficer(ctx: Ctx): void {
  const { world: w, statics } = ctx;
  const o = w.objective;
  const r = w.ranger;
  resolveOfficer(ctx);
  if (o.officerId === -1) return;
  const escorting = statics.def.objective.kind === 'captureAndEscort';
  const name = targetName(statics.def, 'THE OFFICER');

  const captive = o.captives.find((c) => isPrisoner(c.kind));
  if (captive) {
    if (captive.state !== 'following') return;
    const away = dist(captive.x, captive.z, r.x, r.z) > OBJECTIVES.boltDistance;
    captive.awayTicks = away && captive.climbTicks === 0 ? captive.awayTicks + 1 : 0;
    const captorDown = r.status !== 'active' && r.status !== 'boarding' && r.status !== 'extracted';
    if (captorDown || captive.awayTicks >= OBJECTIVES.boltTicks) {
      loseCaptive(ctx, captive);
      if (escorting) failObjective(ctx, `${name} ESCAPED`);
    }
    return;
  }

  const e = officerAtLarge(ctx);
  if (!e) return;
  if (!isAlive(e)) {
    if (escorting) failObjective(ctx, `${name} IS DEAD`);
    return;
  }
  o.surrenderTicks = officerWouldSurrender(ctx) ? o.surrenderTicks + 1 : 0;
  if (o.surrenderTicks >= OBJECTIVES.surrenderTicks) {
    captureOfficer(ctx, e);
    return;
  }

  if (o.officerFleeing && isFighter(e) && e.state !== 'down') {
    if (e.z <= OBJECTIVES.fleeEdgeMargin) {
      w.enemies.splice(w.enemies.indexOf(e), 1);
      ctx.events.push({ type: 'captive', id: e.id, what: 'lost' });
      if (escorting) failObjective(ctx, `${name} GOT AWAY`);
      return;
    }
    // Borrow the soldiers' own flight: due north, and he does not stop to fight.
    if (e.state !== 'flee') {
      e.state = 'flee';
      e.hasSpot = true;
      e.spotX = Math.min(statics.def.sizeX - 2, Math.max(2, e.x));
      e.spotZ = 1.5;
      e.path = [];
    }
    e.stateTicks = Math.max(e.stateTicks, FLEE_KEEPALIVE_TICKS);
  }
}
